import React, { useCallback } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { ClusterMemberDto } from 'src/api/v2';
import ContactLink from '../components/ContactLink';
import { ROUTES } from 'src/navigation';
import { formatOrganizationNumber } from 'src/utils/format';
import { useContacts } from 'src/features/contacts';
import { useTranslation } from 'react-i18next';

const columnHelper = createColumnHelper<ClusterMemberDto>();

export const useClusterMemberColumns = () => {
  const { contacts } = useContacts();
  const { t: tCommon } = useTranslation();

  const getContact = useCallback(
    (contactId: string | undefined) => {
      if (!contactId) return undefined;
      return contacts.find((c) => c.id === contactId);
    },
    [contacts],
  );

  return [
    columnHelper.accessor((row) => row.name, {
      id: 'name',
      header: 'name',
      cell: ({
        getValue,
        row: {
          original: { id },
        },
      }) => {
        return <Link to={ROUTES.RESOURCE_BASE.MEMBERS.DETAILS(id).BASE}>{getValue()}</Link>;
      },
    }),
    columnHelper.accessor((row) => row.organizationNumber, {
      id: 'organizationNumber',
      cell: (info) => formatOrganizationNumber(info.getValue()),
    }),
    columnHelper.accessor(
      (row) => {
        const contact = getContact(row.mainContactId);

        if (!contact) {
          return undefined;
        }

        return `${contact.firstName} ${contact.lastName}`;
      },
      {
        id: 'mainContact',
        header: 'mainContact',
        cell: (info) => {
          return <ContactLink type="name" contactId={info.row.original.mainContactId} />;
        },
      },
    ),
    columnHelper.accessor((row) => getContact(row.mainContactId)?.emailAddress, {
      id: 'mainContactEmail',
      header: 'mainContactEmail',
      cell: (info) => {
        return <ContactLink type="email" contactId={info.row.original.mainContactId} />;
      },
    }),
    columnHelper.accessor((row) => getContact(row.mainContactId)?.mobilePhone, {
      id: 'phone',
      header: 'phone',
      cell: (info) => {
        return <ContactLink type="phone" contactId={info.row.original.mainContactId} />;
      },
    }),

    // Hidden columns, initially
    columnHelper.accessor((row) => row.clusterMemberCategory?.name, {
      id: 'category',
      header: 'category',
      cell: (info) => info.getValue(),
      meta: {
        hide: true,
      },
    }),
    columnHelper.accessor((row) => row.clusterMemberType?.name, {
      id: 'type',
      header: 'type',
      cell: (info) => info.getValue(),
      meta: {
        hide: true,
      },
    }),
    columnHelper.accessor(
      (row) => (row.memberSince ? new Date(row.memberSince).toLocaleDateString() : undefined),
      {
        id: 'memberSince',
        header: 'memberSince',
        cell: (info) => info.getValue(),
        meta: {
          hide: true,
        },
      },
    ),
    columnHelper.accessor((row) => row.interestAreas?.map((ia) => ia.name).join(', '), {
      id: 'interestAreas',
      header: 'interestAreas',
      cell: (info) => info.getValue(),
      meta: {
        hide: true,
      },
    }),
    columnHelper.accessor((row) => row.billingAddress?.street, {
      id: 'address',
      header: 'address',
      cell: (info) => info.getValue(),
      meta: {
        hide: true,
      },
    }),
    columnHelper.accessor((row) => row.billingAddress?.zipCode, {
      id: 'postalCode',
      header: 'postalCode',
      cell: (info) => info.getValue(),
      meta: {
        hide: true,
      },
    }),
    columnHelper.accessor((row) => row.billingAddress?.city, {
      id: 'city',
      header: 'city',
      cell: (info) => info.getValue(),
      meta: {
        hide: true,
      },
    }),
    columnHelper.accessor((row) => tCommon(row.deactivatedDate ? 'yes' : 'no'), {
      id: 'retired',
      header: 'retired',
      cell: (info) => info.getValue(),
      meta: {
        hide: true,
      },
    }),
    columnHelper.accessor(
      (row) => row.deactivatedDate && new Date(row.deactivatedDate).toLocaleDateString(),
      {
        id: 'retiredDate',
        header: 'retiredDate',
        cell: (info) => info.getValue(),
        meta: {
          hide: true,
        },
      },
    ),
    columnHelper.accessor((row) => tCommon(row.isActive ? 'yes' : 'no'), {
      id: 'active',
      header: 'active',
      cell: (info) => info.getValue(),
      meta: {
        hide: true,
      },
    }),
  ];
};
